/**
 * Emergent Marketplace — Public Booking Page (Mode 2)
 * PublicBookingPage.jsx
 * Architectural Layer: Presentation → Public (no auth)
 *
 * Standalone page at 11-11cc.com/booking/{slug}
 * No Emergent account required.
 * Embeddable as an iframe on any external site.
 * After booking: "join Emergent" prompt.
 *
 * Design: Light, airy, trust-building — soft white, sage, warm gold.
 * Font: Cormorant (display) + Plus Jakarta Sans (body)
 */

import { useState, useEffect, useMemo, Fragment } from "react";
import { useParams } from "react-router-dom";
import {
  Star, Clock, Check, ChevronRight,
  ChevronLeft, Lock, Calendar, X, Loader,
  ArrowRight, ClipboardList
} from "lucide-react";
import { CancellationTerms } from "@/components/CancellationTerms";
import { cssUrl } from "@/lib/cssUrl";
import { accentShades } from "@/lib/accent";
import { isAsleep, sleepNote } from "@/lib/sessionSleep";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { stripePromise } from "@/lib/stripe";

/**
 * The card form for someone booking from the public page with no account.
 * The page used to show "Card number — Stripe Elements mounts here" and then record the
 * session as confirmed without taking a penny (owner, Jun 2026).
 */
const CardFields = ({ amount, breakdown, accent, onPaid, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    onError("");
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}${window.location.pathname}` },
      redirect: "if_required",
    });
    setBusy(false);
    if (error) {
      onError(error.message || "Your card could not be charged.");
      return;
    }
    onPaid();
  };

  return (
    <form onSubmit={submit} data-testid="public-booking-card-form">
      {breakdown && (
        <p style={{ fontSize: 12, color: "#5a5a54", margin: "0 0 12px", lineHeight: 1.5 }}
           data-testid="public-booking-card-breakdown">
          Charging <strong>${Number(amount).toFixed(2)}</strong> — ${breakdown.price.toFixed(2)} session
          {" + "}${breakdown.processing_fee.toFixed(2)} card processing ({breakdown.fee_label})
        </p>
      )}
      <PaymentElement />
      <button
        type="submit"
        className="pbp-btn-next"
        style={{ background: accent, marginTop: 16, width: "100%" }}
        disabled={busy || !stripe}
        data-testid="public-booking-confirm-payment-btn"
      >
        {busy
          ? <><Loader size={15} style={{ animation: "spin 1s linear infinite" }} /> Charging…</>
          : <><Lock size={14} /> Pay ${Number(amount).toFixed(2)}</>}
      </button>
    </form>
  );
};

const LOCATION_LABELS = {
  video:     "Video call",
  in_person: "In person",
  phone:     "Phone call",
  custom:    "Details from your practitioner",
};

// What a stranger should read before they book: the kind of session, and the town if they
// have to travel to it. The street address only goes out once the session is confirmed.
const locationLine = (st) => {
  const mode = st?.location_mode || "video";
  if (mode === "in_person") {
    return st?.location_city
      ? `In person · ${st.location_city}`
      : "In person — address sent when your session is confirmed";
  }
  return LOCATION_LABELS[mode];
};

const tzLabel = (tz) => {
  if (!tz) return "";
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "long" })
      .formatToParts(new Date())
      .find(p => p.type === "timeZoneName")?.value || tz.replace(/_/g, " ");
  } catch {
    return tz.replace(/_/g, " ");
  }
};

const GuestCardForm = ({ clientSecret, amount, breakdown, accent, onPaid, onError }) => (
  <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "stripe" } }}>
    <CardFields amount={amount} breakdown={breakdown} accent={accent} onPaid={onPaid} onError={onError} />
  </Elements>
);

const API = "/api";

const apiFetchPublic = async (path, options = {}) => {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
};

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Plus+Jakarta+Sans:wght@300;400;500;600&display=swap');

  .pbp-root {
    --sage:        #2d6b4a;
    --sage-light:  #4a8a68;
    --sage-bg:     #eef5f1;
    --sage-border: #b8d8c4;
    --gold:        #b8920a;
    --gold-bg:     #fef8e8;
    --gold-border: #e8d080;
    --white:       #ffffff;
    --off-white:   #f8f6f2;
    --border:      #e4ddd4;
    --ink:         #1a1a18;
    --ink-mid:     #5a5a54;
    --ink-light:   #9a9a90;
    font-family: 'Plus Jakarta Sans', sans-serif;
    background: var(--off-white);
    color: var(--ink);
    min-height: 100vh;
  }

  /* Embed-safe reset */
  .pbp-root *, .pbp-root *::before, .pbp-root *::after {
    box-sizing: border-box;
  }

  /* ── Layout ─────────────────────────────────────────────────────────────── */
  .pbp-container {
    max-width: 100%;
    width: 100%;
    margin: 0 auto;
    padding: 0 0 60px;
  }

  /* ── Practitioner header ─────────────────────────────────────────────── */
  .pbp-hero {
    background: var(--white);
    border-bottom: 1px solid var(--border);
    padding: 28px 24px 20px;
    text-align: center;
  }

  .pbp-avatar {
    width: 72px; height: 72px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--sage), #1b4a34);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Cormorant', serif;
    font-size: 30px;
    font-weight: 600;
    margin: 0 auto 16px;
    border: 3px solid var(--sage-border);
    overflow: hidden;
  }

  .pbp-avatar-img {
    width: 100%; height: 100%;
    object-fit: cover;
  }

  /* Wide banner with the photo overlapping it */
  .pbp-banner {
    height: 150px;
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
    border-bottom: 1px solid var(--border);
  }

  .pbp-avatar.pbp-avatar-over {
    margin: -38px 0 12px;
    width: 82px; height: 82px;
    border-width: 4px;
    box-shadow: 0 6px 18px rgba(26,26,24,0.14);
  }

  /* Session details panel */
  .pbp-details-scrim {
    position: fixed;
    inset: 0;
    background: rgba(26,26,24,0.45);
    backdrop-filter: blur(3px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    z-index: 60;
    animation: pbpFade 0.16s ease-out;
  }

  @keyframes pbpFade { from { opacity: 0 } to { opacity: 1 } }

  .pbp-details-card {
    position: relative;
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 24px 22px 20px;
    max-width: 460px;
    width: 100%;
    max-height: 82vh;
    overflow-y: auto;
    box-shadow: 0 24px 60px rgba(26,26,24,0.2);
    animation: pbpRise 0.2s ease-out;
  }

  @keyframes pbpRise { from { transform: translateY(10px); opacity: 0 } to { transform: none; opacity: 1 } }

  .pbp-details-close {
    position: absolute;
    top: 12px; right: 12px;
    background: none;
    border: 1px solid var(--border);
    border-radius: 8px;
    width: 28px; height: 28px;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    color: var(--ink-mid);
    transition: border-color 0.15s, color 0.15s;
  }

  .pbp-details-close:hover { border-color: var(--ink-mid); color: var(--ink); }

  .pbp-details-title {
    font-family: 'Cormorant', serif;
    font-size: 23px;
    font-weight: 600;
    margin: 0 28px 4px 0;
    color: var(--ink);
  }

  .pbp-details-meta {
    font-size: 12px;
    color: var(--ink-light);
    margin: 0 0 14px;
    letter-spacing: 0.02em;
  }

  .pbp-details-body p {
    font-size: 14px;
    line-height: 1.7;
    color: var(--ink-mid);
    margin: 0 0 12px;
  }

  .pbp-name {
    font-family: 'Cormorant', serif;
    font-size: 26px;
    font-weight: 600;
    color: var(--ink);
    margin: 0 0 8px;
  }

  .pbp-bio {
    font-size: 14px;
    color: var(--ink-mid);
    line-height: 1.65;
    margin: 0 0 12px;
    max-width: 360px;
    margin-left: auto;
    margin-right: auto;
  }

  .pbp-rating {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 13px;
    color: var(--gold);
    font-weight: 600;
  }

  /* Powered by banner */
  .pbp-powered {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 0 0;
    font-size: 11px;
    color: var(--ink-light);
    letter-spacing: 0.04em;
    border-top: 1px solid var(--border);
    margin-top: 14px;
  }

  .pbp-powered-logo {
    font-family: 'Cormorant', serif;
    font-size: 13px;
    font-weight: 600;
    color: var(--sage);
    font-style: italic;
  }

  /* ── Steps ───────────────────────────────────────────────────────────── */
  .pbp-steps {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px 24px;
    background: var(--white);
    border-bottom: 1px solid var(--border);
  }

  .pbp-step-dot {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  .pbp-step-circle {
    width: 28px; height: 28px;
    border-radius: 50%;
    border: 2px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    color: var(--ink-light);
    background: var(--white);
    transition: all 0.2s;
  }

  .pbp-step-circle.active {
    background: var(--sage);
    border-color: var(--sage);
    color: white;
    box-shadow: 0 0 0 3px rgba(45,107,74,0.15);
  }

  .pbp-step-circle.done {
    background: var(--sage);
    border-color: var(--sage);
    color: white;
  }

  .pbp-step-label {
    font-size: 10px;
    color: var(--ink-light);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-weight: 600;
  }

  .pbp-step-label.active { color: var(--sage); }

  .pbp-step-line {
    flex: 1;
    height: 2px;
    background: var(--border);
    margin-bottom: 16px;
  }

  .pbp-step-line.done { background: var(--sage); }

  /* ── Step body ───────────────────────────────────────────────────────── */
  .pbp-body { padding: 24px; }

  .pbp-section-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--ink-light);
    margin: 0 0 12px;
  }

  /* Session picker */
  .pbp-session-list { display: flex; flex-direction: column; gap: 10px; }
  .pbp-empty-sessions {
    padding: 22px 18px;
    text-align: center;
    font-size: 13px;
    color: var(--ink-light, #6b7671);
    background: var(--off-white, #f6f5f1);
    border: 1px dashed var(--border, #e2ded4);
    border-radius: 10px;
  }

  .pbp-session-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    background: var(--white);
    border: 1.5px solid var(--border);
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.15s;
  }

  .pbp-session-row:hover { border-color: var(--sage-light); }

  .pbp-session-row.selected {
    border-color: var(--sage);
    background: rgba(45,107,74,0.03);
  }

  .pbp-session-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--ink);
    margin: 0 0 4px;
  }

  .pbp-session-meta {
    font-size: 12px;
    color: var(--ink-light);
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .pbp-session-price {
    font-family: 'Cormorant', serif;
    font-size: 22px;
    font-weight: 600;
    color: var(--sage);
    white-space: nowrap;
  }

  /* Date/time picker */
  .pbp-calendar-placeholder {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 20px;
  }

  .pbp-cal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .pbp-cal-month {
    font-family: 'Cormorant', serif;
    font-size: 18px;
    font-weight: 600;
    color: var(--ink);
  }

  .pbp-cal-nav {
    background: none;
    border: 1px solid var(--border);
    border-radius: 6px;
    width: 28px; height: 28px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--ink-mid);
    transition: border-color 0.15s;
  }

  .pbp-cal-nav:hover { border-color: var(--sage); color: var(--sage); }

  .pbp-cal-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
    text-align: center;
  }

  .pbp-cal-dow {
    font-size: 10px;
    font-weight: 600;
    color: var(--ink-light);
    text-transform: uppercase;
    padding: 4px 0;
  }

  .pbp-cal-day {
    padding: 7px 0;
    border-radius: 8px;
    font-size: 13px;
    cursor: pointer;
    border: none;
    background: none;
    color: var(--ink-light);
    transition: all 0.12s;
  }

  .pbp-cal-day:hover:not(:disabled) { background: var(--sage-bg); }
  .pbp-cal-day:disabled { color: #d5d0c8; cursor: default; opacity: 0.4; }
  .pbp-cal-day.empty { visibility: hidden; }
  .pbp-cal-day.today { font-weight: 700; }

  .pbp-cal-day.selected {
    background: var(--sage);
    color: white;
    font-weight: 600;
  }

  .pbp-cal-day.has-slots {
    color: var(--sage);
    font-weight: 700;
    background: var(--sage-bg);
  }

  .pbp-time-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink-light);
    margin: 16px 0 10px;
  }

  .pbp-time-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
  }

  .pbp-time-btn {
    padding: 9px 4px;
    border: 1.5px solid var(--border);
    border-radius: 8px;
    background: var(--white);
    color: var(--ink);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.12s;
  }

  .pbp-time-btn:hover { border-color: var(--sage-light); }

  .pbp-time-btn.selected {
    background: var(--sage);
    border-color: var(--sage);
    color: white;
    font-weight: 600;
  }

  .pbp-slots-loading {
    text-align: center;
    padding: 16px;
    color: var(--ink-light);
    font-size: 13px;
  }

  /* Guest info form */
  .pbp-field { margin-bottom: 14px; }

  .pbp-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: var(--ink-mid);
    margin-bottom: 6px;
    letter-spacing: 0.04em;
  }

  .pbp-input {
    width: 100%;
    background: var(--white);
    border: 1.5px solid var(--border);
    border-radius: 9px;
    padding: 11px 14px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 14px;
    color: var(--ink);
    outline: none;
    transition: border-color 0.15s;
  }

  .pbp-input:focus {
    border-color: var(--sage);
    box-shadow: 0 0 0 3px rgba(45,107,74,0.08);
  }

  .pbp-row { display: flex; gap: 10px; }
  .pbp-row .pbp-field { flex: 1; }

  /* Stripe element area */
  .pbp-pay-error {
    font-size: 12.5px;
    color: #b4341f;
    background: #fdf1ee;
    border: 1px solid #f3d3cb;
    border-radius: 8px;
    padding: 9px 12px;
    margin-top: 12px;
  }
  .pbp-stripe-area {
    background: var(--white);
    border: 1.5px solid var(--border);
    border-radius: 9px;
    padding: 13px 14px;
    font-size: 14px;
    color: var(--ink-light);
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }

  /* Order summary */
  .pbp-order-summary {
    background: var(--sage-bg);
    border: 1px solid var(--sage-border);
    border-radius: 12px;
    padding: 14px 16px;
    margin-bottom: 20px;
  }

  .pbp-order-row {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    color: var(--ink-mid);
    padding: 3px 0;
  }

  .pbp-order-total {
    display: flex;
    justify-content: space-between;
    font-size: 16px;
    font-weight: 700;
    color: var(--ink);
    padding-top: 8px;
    margin-top: 6px;
    border-top: 1px solid var(--sage-border);
  }

  /* Nav buttons */
  .pbp-btn-row {
    display: flex;
    gap: 10px;
    margin-top: 24px;
  }

  .pbp-btn-back {
    padding: 12px 16px;
    border-radius: 10px;
    border: 1.5px solid var(--border);
    background: none;
    color: var(--ink-mid);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: border-color 0.15s;
  }

  .pbp-btn-back:hover { border-color: var(--ink-mid); }

  .pbp-btn-next {
    flex: 1;
    padding: 12px;
    border-radius: 10px;
    background: var(--sage);
    border: none;
    color: white;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: background 0.15s;
  }

  .pbp-btn-next:hover { background: var(--sage-light); }
  .pbp-btn-next:disabled { opacity: 0.5; cursor: not-allowed; }

  /* ── Success + Join prompt ────────────────────────────────────────────── */
  .pbp-success {
    text-align: center;
    padding: 40px 24px;
  }

  .pbp-success-circle {
    width: 80px; height: 80px;
    border-radius: 50%;
    background: var(--sage-bg);
    border: 2px solid var(--sage-border);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;
    color: var(--sage);
  }

  .pbp-success-title {
    font-family: 'Cormorant', serif;
    font-size: 28px;
    font-weight: 600;
    color: var(--ink);
    margin: 0 0 10px;
  }

  .pbp-success-body {
    font-size: 14px;
    color: var(--ink-mid);
    line-height: 1.65;
    margin: 0 0 32px;
    max-width: 340px;
    margin-left: auto;
    margin-right: auto;
  }

  .pbp-confirmation-box {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 20px;
    text-align: left;
    margin-bottom: 28px;
  }

  .pbp-conf-row {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    padding: 5px 0;
    border-bottom: 1px solid var(--off-white);
  }

  .pbp-conf-row:last-child { border-bottom: none; }
  .pbp-conf-label { color: var(--ink-light); }
  .pbp-conf-value { font-weight: 600; color: var(--ink); }

  /* Join Emergent CTA */
  .pbp-join-card {
    background: linear-gradient(135deg, var(--sage), #1b4a34);
    border-radius: 16px;
    padding: 24px;
    text-align: center;
    color: white;
    margin-bottom: 16px;
  }

  .pbp-join-eyebrow {
    font-size: 11px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.7);
    margin-bottom: 8px;
  }

  .pbp-join-title {
    font-family: 'Cormorant', serif;
    font-size: 22px;
    font-weight: 600;
    font-style: italic;
    margin: 0 0 10px;
  }

  .pbp-join-body {
    font-size: 13px;
    color: rgba(255,255,255,0.8);
    line-height: 1.6;
    margin: 0 0 18px;
  }

  .pbp-join-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    border-radius: 10px;
    background: rgba(255,255,255,0.15);
    border: 1px solid rgba(255,255,255,0.3);
    color: white;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
    text-decoration: none;
  }

  .pbp-join-btn:hover { background: rgba(255,255,255,0.25); }

  .pbp-skip-link {
    font-size: 12px;
    color: var(--ink-light);
    text-decoration: none;
    cursor: pointer;
    display: block;
    text-align: center;
  }

  .pbp-skip-link:hover { color: var(--ink-mid); }
`;

const STEPS = ["Session", "Details", "Payment", "Confirmed"];

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOWS = ["Su","Mo","Tu","We","Th","Fr","Sa"];

// ─── Main Component ───────────────────────────────────────────────────────────

export default function PublicBookingPage({ isEmbed = false }) {
  const { slug } = useParams();
  const bookingSlug = slug || "aria-santos";
  const [profile, setProfile]               = useState(null);
  const [step, setStep]                     = useState(0);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedDate, setSelectedDate]     = useState(null);
  const [guest, setGuest]                   = useState({ name: "", email: "", phone: "" });
  const [processing, setProcessing]         = useState(false);
  const [pendingBooking, setPendingBooking] = useState(null);   // held until the card clears
  const [payError, setPayError]             = useState("");
  const [bookingRef, setBookingRef]         = useState(null);
  const [quote, setQuote]                   = useState(null);   // price + processing, from the server
  const [paidAmount, setPaidAmount]         = useState(null);   // what the card was actually charged
  const [joinDismissed, setJoinDismissed]   = useState(false);
  const [detailsFor, setDetailsFor]         = useState(null);
  const [loading, setLoading]               = useState(true);

  // Calendar state
  const [calMonth, setCalMonth]             = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() }; });
  const [selectedDateStr, setSelectedDateStr] = useState(null);
  const [slots, setSlots]                   = useState([]);
  const [slotsLoading, setSlotsLoading]     = useState(false);
  const [slotsByDate, setSlotsByDate]       = useState({});

  const upd = (f) => setGuest(g => ({ ...g, ...f }));

  useEffect(() => {
    setLoading(true);
    apiFetchPublic(`/booking/${bookingSlug}`)
      .then(d => setProfile(d.profile))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [bookingSlug]);

  // Fetch slots when session is selected or month changes
  useEffect(() => {
    if (!selectedSession || !profile) return;
    const duration = selectedSession.total_duration || selectedSession.healing_duration || 60;
    const fromDate = new Date(calMonth.year, calMonth.month, 1);
    const toDate = new Date(calMonth.year, calMonth.month + 1, 0);
    // Ensure we don't request past dates
    const today = new Date();
    today.setHours(0,0,0,0);
    const effectiveFrom = fromDate < today ? today : fromDate;
    if (effectiveFrom > toDate) return;

    const fromStr = effectiveFrom.toISOString().split('T')[0];
    const toStr = toDate.toISOString().split('T')[0];

    setSlotsLoading(true);
    apiFetchPublic(`/availability/${profile.vendor_id}/slots?from_date=${fromStr}&to_date=${toStr}&session_type_id=${selectedSession.id}&duration_minutes=${duration}`)
      .then(d => {
        setSlots(d.slots || []);
        // Group by date
        const byDate = {};
        (d.slots || []).forEach(s => {
          if (!byDate[s.date_str]) byDate[s.date_str] = [];
          byDate[s.date_str].push(s);
        });
        setSlotsByDate(byDate);
      })
      .catch(() => { setSlots([]); setSlotsByDate({}); })
      .finally(() => setSlotsLoading(false));
  }, [selectedSession, calMonth, profile]);

  // What the card will actually be charged. Asked of the server rather than worked out
  // here, so the figure on the page can never drift from the figure in the PaymentIntent.
  useEffect(() => {
    if (!selectedSession) { setQuote(null); return; }
    apiFetchPublic(`/booking/${bookingSlug}/quote?session_type_id=${encodeURIComponent(selectedSession.id)}`)
      .then(setQuote)
      .catch(() => setQuote(null));
  }, [selectedSession, bookingSlug]);

  // Calendar helpers
  const calDays = useMemo(() => {
    const first = new Date(calMonth.year, calMonth.month, 1);
    const lastDay = new Date(calMonth.year, calMonth.month + 1, 0).getDate();
    const startDow = first.getDay();
    const days = [];
    for (let i = 0; i < startDow; i++) days.push(null);
    for (let d = 1; d <= lastDay; d++) days.push(d);
    return days;
  }, [calMonth]);

  const todayStr = new Date().toISOString().split('T')[0];
  const timeSlotsForDate = selectedDateStr ? (slotsByDate[selectedDateStr] || []) : [];

  const pay = async () => {
    setProcessing(true);
    try {
      const result = await apiFetchPublic(`/booking/${bookingSlug}/book`, {
        method: "POST",
        body: JSON.stringify({
          vendor_id: profile?.vendor_id || "",
          session_type_id: selectedSession?.id || selectedSession,
          scheduled_at: selectedDate || new Date().toISOString(),
          timezone: profile?.timezone || "America/New_York",
          guest_name: guest.name,
          guest_email: guest.email,
          guest_phone: guest.phone,
        }),
      });
      // A free session is done. A paid one is only held until the card goes through, so hand
      // over to Stripe Elements instead of pretending it is booked.
      if (result.requires_payment && result.client_secret) {
        setPendingBooking(result);
        return;
      }
      setPaidAmount(0);
      setBookingRef(result.id?.slice(0, 8)?.toUpperCase() || "CONFIRMED");
      setStep(3);
    } catch (e) {
      setPayError(e.message || "Booking failed");
    } finally {
      setProcessing(false);
    }
  };

  const onPaid = async (booking) => {
    try {
      await apiFetchPublic(`/booking/${bookingSlug}/confirm-payment`, {
        method: "POST",
        body: JSON.stringify({ booking_id: booking.id }),
      });
    } catch {
      // Stripe has the money either way; the webhook confirms it. Never strand them here.
    }
    setBookingRef(booking.id?.slice(0, 8)?.toUpperCase() || "CONFIRMED");
    setPaidAmount(booking.charge_total ?? quote?.total ?? null);
    setPendingBooking(null);
    setStep(3);
  };

  const accent = profile?.widget_accent || "#2d6b4a";
  // The page used to hardcode its green in CSS variables, so a practitioner who picked blue
  // still got a green step indicator, ring and selected row (owner, Jun 2026: the colour
  // "doesn't work"). Feed the chosen colour into the same variables instead.
  const shades = accentShades(accent);
  const accentVars = {
    "--sage":        shades.base,
    "--sage-light":  shades.light,
    "--sage-bg":     shades.tint,
    "--sage-border": shades.border,
  };
  const accentDeep = shades.deep;
  // A session may carry the practitioner's own scheduling link instead of being booked here.
  const inAppSessions    = (profile?.session_types || []).filter(st => !st.external_url);
  const externalSessions = (profile?.session_types || []).filter(st => st.external_url);

  if (loading) return (
    <div style={{ padding: 40, textAlign: "center", color: "#8b80a8", fontFamily: "Plus Jakarta Sans" }}>
      Loading booking page...
    </div>
  );

  if (!profile) return (
    <div style={{ padding: 40, textAlign: "center", color: "#8b80a8", fontFamily: "Plus Jakarta Sans" }}>
      Booking page not found
    </div>
  );

  return (
    <>
      <style>{styles}</style>
      <div className="pbp-root" style={accentVars}>
        <div className="pbp-container">

          {/* Practitioner hero */}
          <div className="pbp-hero" style={profile.banner_url ? { padding: 0, textAlign: "left" } : undefined}>
            {profile.banner_url && (
              <div className="pbp-banner" data-testid="public-booking-banner"
                   style={{ backgroundImage: cssUrl(profile.banner_url.startsWith('http')
                     ? profile.banner_url
                     : `${process.env.REACT_APP_BACKEND_URL}${profile.banner_url}`) }} />
            )}
            <div style={profile.banner_url ? { padding: "0 24px 20px" } : undefined}>
            <div className={`pbp-avatar${profile.banner_url ? " pbp-avatar-over" : ""}`}
                 style={{ background: `linear-gradient(135deg, ${accent}, ${accentDeep})` }}>
              {profile.avatar_url ? (
                <img
                  src={profile.avatar_url.startsWith('http')
                    ? profile.avatar_url
                    : `${process.env.REACT_APP_BACKEND_URL}${profile.avatar_url}`}
                  alt={profile.display_name}
                  className="pbp-avatar-img"
                />
              ) : profile.display_name[0]}
            </div>
            <h1 className="pbp-name">{profile.display_name}</h1>
            <p className="pbp-bio" style={profile.banner_url ? { marginLeft: 0 } : undefined}>{profile.bio}</p>
            {profile.rating_avg > 0 && (
              <div className="pbp-rating">
                <Star size={13} fill="currentColor" />
                {profile.rating_avg.toFixed(1)} · {profile.rating_count} reviews
              </div>
            )}
            <div className="pbp-powered">
              <span>Booking powered by</span>
              <span className="pbp-powered-logo">11/11 Collective Connection</span>
            </div>
            </div>
          </div>

          {/* What a session actually is — behind a link, so the list stays readable */}
          {detailsFor && (
            <div className="pbp-details-scrim" onClick={() => setDetailsFor(null)}
                 data-testid="public-session-details-panel">
              <div className="pbp-details-card" onClick={e => e.stopPropagation()}>
                <button className="pbp-details-close" onClick={() => setDetailsFor(null)}
                        aria-label="Close" data-testid="public-session-details-close">
                  <X size={16} />
                </button>
                <p className="pbp-details-title">{detailsFor.name}</p>
                <p className="pbp-details-meta">
                  {detailsFor.healing_duration || detailsFor.total_duration} min ·{" "}
                  {locationLine(detailsFor)} ·{" "}
                  {detailsFor.price > 0 ? `$${detailsFor.price}` : "Free"}
                </p>
                <div className="pbp-details-body">
                  {String(detailsFor.description || "").split(/\n{1,}/).map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
                <button
                  className="pbp-btn-next"
                  style={{ background: accent, width: "100%" }}
                  onClick={() => {
                    setSelectedSession(detailsFor);
                    setSelectedDate(null);
                    setSelectedDateStr(null);
                    setDetailsFor(null);
                  }}
                  data-testid="public-session-details-choose"
                >
                  Choose this session <ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}

          {/* Step indicator */}
          {step < 3 && (
            <div className="pbp-steps">
              {STEPS.slice(0, 3).map((label, i) => (
                <Fragment key={label}>
                  <div className="pbp-step-dot">
                    <div className={`pbp-step-circle ${i === step ? "active" : i < step ? "done" : ""}`}>
                      {i < step ? <Check size={13} /> : i + 1}
                    </div>
                    <span className={`pbp-step-label ${i === step ? "active" : ""}`}>{label}</span>
                  </div>
                  {i < 2 && (
                    <div className={`pbp-step-line ${i < step ? "done" : ""}`} />
                  )}
                </Fragment>
              ))}
            </div>
          )}

          {/* ── Step 0: Session selection + Calendar ── */}
          {step === 0 && (
            <div className="pbp-body">
              <p className="pbp-section-label">Choose a session</p>
              <div className="pbp-session-list">
                {inAppSessions.length === 0 && externalSessions.length === 0 && (
                  // Publishing with no sessions listed used to render an empty list above a
                  // dead Continue button.
                  <div className="pbp-empty-sessions" data-testid="public-booking-no-sessions">
                    {profile.display_name || "This practitioner"} hasn’t listed any sessions yet.
                    Please check back soon.
                  </div>
                )}
                {inAppSessions.map(st => (
                  <div
                    key={st.id}
                    className={`pbp-session-row ${selectedSession?.id === st.id ? "selected" : ""}`}
                    onClick={() => {
                      if (isAsleep(st)) return;   // listed, so they can ask — but not bookable
                      setSelectedSession(st); setSelectedDate(null); setSelectedDateStr(null);
                    }}
                    style={isAsleep(st) ? { opacity: 0.6, cursor: "default" } : undefined}
                  >
                    <div>
                      <p className="pbp-session-title">{st.name}</p>
                      <div className="pbp-session-meta">
                        {isAsleep(st) && (
                          <span style={{ fontWeight: 600 }}
                                data-testid={`public-session-asleep-${st.id}`}>
                            {sleepNote(st)}
                          </span>
                        )}
                        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <Clock size={11} /> {st.healing_duration || st.total_duration} min
                        </span>
                        {/* Every session was labelled "Video call" here, which was simply
                            untrue for an in-person one. Now it says what the practitioner set. */}
                        <span style={{ display: "flex", alignItems: "center", gap: 4 }}
                              data-testid={`public-session-location-${st.id}`}>
                          {locationLine(st)}
                        </span>
                        {st.description && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setDetailsFor(st); }}
                            style={{
                              background: "none", border: "none", padding: 0, cursor: "pointer",
                              fontSize: 12, fontWeight: 600, color: accent,
                              textDecoration: "underline", fontFamily: "inherit",
                            }}
                            data-testid={`public-session-details-${st.id}`}
                          >
                            Details
                          </button>
                        )}
                        {(() => {
                          // Heads-up before they commit: this session asks for paperwork.
                          const count = ((profile.practice_intake_forms || []).length
                                       + (st.intake_forms || []).length);
                          return count > 0 ? (
                            <span style={{ display: "flex", alignItems: "center", gap: 4 }}
                                  data-testid={`public-session-intake-${st.id}`}>
                              <ClipboardList size={11} /> {count} form{count > 1 ? "s" : ""} to complete
                            </span>
                          ) : null;
                        })()}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span className="pbp-session-price">{st.price > 0 ? `$${st.price}` : "Free"}</span>
                      <div style={{
                        width: 20, height: 20, borderRadius: "50%",
                        border: `2px solid ${selectedSession?.id === st.id ? accent : "var(--border)"}`,
                        background: selectedSession?.id === st.id ? accent : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {selectedSession?.id === st.id && <Check size={11} color="white" />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Sessions this practitioner takes on her own platform */}
              {externalSessions.length > 0 && (
                <div style={{ marginTop: 18 }} data-testid="public-external-session-list">
                  <p className="pbp-section-label">
                    Booked on {profile.external_agent || "the practitioner's own booking page"}
                  </p>
                  {externalSessions.map(st => (
                    <div key={st.id} className="pbp-session-row" style={{ cursor: "default" }}
                         data-testid={`public-external-session-${st.id}`}>
                      <div>
                        <p className="pbp-session-title">{st.name}</p>
                        <div className="pbp-session-meta">
                          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <Clock size={11} /> {st.healing_duration || st.total_duration} min
                          </span>
                          {st.description && (
                            <button
                              type="button"
                              onClick={() => setDetailsFor(st)}
                              style={{
                                background: "none", border: "none", padding: 0, cursor: "pointer",
                                fontSize: 12, fontWeight: 600, color: accent,
                                textDecoration: "underline", fontFamily: "inherit",
                              }}
                              data-testid={`public-external-details-${st.id}`}
                            >
                              Details
                            </button>
                          )}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <span className="pbp-session-price">{st.price > 0 ? `$${st.price}` : "Free"}</span>
                        <a
                          href={st.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="pbp-btn-next"
                          style={{ background: accent, width: "auto", padding: "9px 16px",
                                   textDecoration: "none", fontSize: 13 }}
                          data-testid={`public-external-book-${st.id}`}
                        >
                          Book <ChevronRight size={14} />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {inAppSessions.some(st => st.price > 0) && (
                <p style={{ fontSize: 11, color: "var(--ink-light)", margin: "8px 2px 0", lineHeight: 1.5 }}
                   data-testid="public-booking-fee-notice">
                  Prices are the session rate. Card processing (3% + $0.30) is added at checkout and
                  shown in full before you pay.
                </p>
              )}

              {/* Availability Calendar */}
              {selectedSession && (
                <div className="pbp-calendar-placeholder" style={{ marginTop: 16 }}>
                  {/* Month header */}
                  <div className="pbp-cal-header">
                    <button className="pbp-cal-nav" onClick={() => setCalMonth(m => {
                      const d = new Date(m.year, m.month - 1, 1);
                      return { year: d.getFullYear(), month: d.getMonth() };
                    })}>
                      <ChevronLeft size={14} />
                    </button>
                    <span className="pbp-cal-month">{MONTHS[calMonth.month]} {calMonth.year}</span>
                    <button className="pbp-cal-nav" onClick={() => setCalMonth(m => {
                      const d = new Date(m.year, m.month + 1, 1);
                      return { year: d.getFullYear(), month: d.getMonth() };
                    })}>
                      <ChevronRight size={14} />
                    </button>
                  </div>

                  {/* Day of week headers */}
                  <div className="pbp-cal-grid">
                    {DOWS.map(d => <div key={d} className="pbp-cal-dow">{d}</div>)}
                    {calDays.map((day, i) => {
                      if (day === null) return <div key={`e${i}`} className="pbp-cal-day empty" />;
                      const dateStr = `${calMonth.year}-${String(calMonth.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const isPast = dateStr < todayStr;
                      const hasSlots = !!slotsByDate[dateStr];
                      const isSelected = dateStr === selectedDateStr;
                      const isToday = dateStr === todayStr;
                      return (
                        <button
                          key={dateStr}
                          className={`pbp-cal-day${isSelected ? ' selected' : ''}${hasSlots ? ' has-slots' : ''}${isToday ? ' today' : ''}`}
                          disabled={isPast || !hasSlots}
                          data-testid={`public-cal-day-${dateStr}`}
                          data-has-slots={hasSlots ? "true" : "false"}
                          onClick={() => { setSelectedDateStr(dateStr); setSelectedDate(null); }}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>

                  {slotsLoading && <div className="pbp-slots-loading"><Loader size={14} style={{ animation: "spin 1s linear infinite", display: "inline-block" }} /> Loading availability...</div>}

                  {/* Time slots for selected date */}
                  {selectedDateStr && timeSlotsForDate.length > 0 && (
                    <>
                      <p className="pbp-time-label">
                        Available times
                        {profile.timezone && (
                          <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}
                                data-testid="public-booking-timezone">
                            {" "}· shown in {tzLabel(profile.timezone)}
                          </span>
                        )}
                      </p>
                      <div className="pbp-time-grid">
                        {timeSlotsForDate.map(slot => (
                          <button
                            key={slot.datetime_utc}
                            className={`pbp-time-btn${selectedDate === slot.datetime_utc ? ' selected' : ''}`}
                            onClick={() => setSelectedDate(slot.datetime_utc)}
                          >
                            {slot.time_label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  {selectedDateStr && !slotsLoading && timeSlotsForDate.length === 0 && (
                    <p className="pbp-slots-loading">No available times on this date</p>
                  )}
                </div>
              )}

              <div className="pbp-btn-row">
                <button
                  className="pbp-btn-next"
                  style={{ background: accent }}
                  disabled={!selectedSession || !selectedDate}
                  onClick={() => setStep(1)}
                >
                  Continue <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 1: Guest details ── */}
          {step === 1 && (
            <div className="pbp-body">
              <p className="pbp-section-label">Your details</p>

              <div className="pbp-field">
                <label className="pbp-label">Full name *</label>
                <input className="pbp-input" placeholder="Your name" value={guest.name} onChange={e => upd({ name: e.target.value })} />
              </div>
              <div className="pbp-field">
                <label className="pbp-label">Email address *</label>
                <input className="pbp-input" type="email" placeholder="you@email.com" value={guest.email} onChange={e => upd({ email: e.target.value })} />
              </div>
              <div className="pbp-field">
                <label className="pbp-label">Phone <span style={{ fontWeight: 400, color: "var(--ink-light)" }}>(optional)</span></label>
                <input className="pbp-input" type="tel" placeholder="+1 555 000 0000" value={guest.phone} onChange={e => upd({ phone: e.target.value })} />
              </div>

              <div className="pbp-btn-row">
                <button className="pbp-btn-back" onClick={() => setStep(0)}>
                  <ChevronLeft size={15} /> Back
                </button>
                <button
                  className="pbp-btn-next"
                  style={{ background: accent }}
                  disabled={!guest.name || !guest.email}
                  onClick={() => setStep(2)}
                >
                  Continue <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2: Payment ── */}
          {step === 2 && (
            <div className="pbp-body">
              <div className="pbp-order-summary">
                <div className="pbp-order-row">
                  <span>{selectedSession?.name}</span>
                  <span>{selectedSession?.healing_duration || selectedSession?.total_duration} min</span>
                </div>
                <div className="pbp-order-row">
                  <span>With {profile.display_name}</span>
                  <span>{locationLine(selectedSession || {})}</span>
                </div>
                {profile.timezone && (
                  <div className="pbp-order-row">
                    <span>Time zone</span>
                    <span data-testid="public-booking-summary-timezone">{tzLabel(profile.timezone)}</span>
                  </div>
                )}
                {/* The full breakdown. This block showed the session price alone as "Total"
                    and then charged 3% + $0.30 on top of it (owner, Jun 2026). */}
                {(quote?.price ?? selectedSession?.price ?? 0) > 0 ? (
                  <div data-testid="public-booking-breakdown">
                    <div className="pbp-order-row" style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--sage-border)" }}>
                      <span>Session</span>
                      <span data-testid="public-booking-price">
                        ${(quote?.price ?? selectedSession?.price).toFixed(2)}
                      </span>
                    </div>
                    <div className="pbp-order-row">
                      <span>Card processing ({quote?.fee_label || "3% + $0.30"})</span>
                      <span data-testid="public-booking-processing-fee">
                        ${(quote?.processing_fee ?? 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="pbp-order-total">
                      <span>Total due today</span>
                      <span data-testid="public-booking-total">
                        ${(quote?.total ?? selectedSession?.price).toFixed(2)}
                      </span>
                    </div>
                    <p style={{ fontSize: 11, color: "var(--ink-light)", margin: "8px 0 0", lineHeight: 1.5 }}>
                      Card processing is what the payment network charges to take a card. It is added
                      on top so {profile.display_name} receives their full session rate. Sales tax, where
                      it applies, is calculated at payment.
                    </p>
                  </div>
                ) : (
                  <div className="pbp-order-total">
                    <span>Total due today</span>
                    <span data-testid="public-booking-total">Free</span>
                  </div>
                )}
              </div>

              <p className="pbp-section-label">Payment details</p>
              {/* Where the terms belong: at the moment money is involved, as in the app.
                  They used to sit in the page header, above the session list, so the first
                  thing anyone read was the refund policy. */}
              <CancellationTerms policy={profile.cancellation_policy} compact />
              {pendingBooking ? (
                <GuestCardForm
                  clientSecret={pendingBooking.client_secret}
                  amount={pendingBooking.charge_total ?? quote?.total ?? selectedSession?.price}
                  breakdown={quote}
                  accent={accent}
                  onPaid={() => onPaid(pendingBooking)}
                  onError={setPayError}
                />
              ) : (
                <div className="pbp-stripe-area">
                  <Lock size={14} color="var(--ink-light)" />
                  <span>Your card details are entered on the next step, handled by Stripe.</span>
                </div>
              )}
              {payError && (
                <p className="pbp-pay-error" data-testid="public-booking-pay-error">{payError}</p>
              )}
              <p style={{ fontSize: 11, color: "var(--ink-light)", margin: "6px 0 20px", lineHeight: 1.5 }}>
                Payments secured by Stripe. {profile.display_name} will never see your card details.
              </p>

              {!pendingBooking && (
              <div className="pbp-btn-row">
                <button className="pbp-btn-back" onClick={() => setStep(1)}>
                  <ChevronLeft size={15} /> Back
                </button>
                <button
                  className="pbp-btn-next"
                  style={{ background: accent }}
                  onClick={pay}
                  disabled={processing}
                  data-testid="public-booking-pay-btn"
                >
                  {processing
                    ? <><Loader size={15} style={{ animation: "spin 1s linear infinite" }} /> Processing...</>
                    : <><Lock size={14} /> {(quote?.total ?? selectedSession?.price ?? 0) > 0
                        ? `Pay $${(quote?.total ?? selectedSession?.price).toFixed(2)}`
                        : "Confirm booking"}</>
                  }
                </button>
              </div>
              )}
            </div>
          )}

          {/* ── Step 3: Confirmed + Join prompt ── */}
          {step === 3 && (
            <div className="pbp-body">
              <div className="pbp-success">
                <div className="pbp-success-circle" style={{ background: `${accent}18`, borderColor: `${accent}44` }}>
                  <Check size={36} color={accent} strokeWidth={2.5} />
                </div>
                <h2 className="pbp-success-title">You're booked!</h2>
                <p className="pbp-success-body">
                  A confirmation has been sent to <strong>{guest.email}</strong>. {profile.display_name} will confirm your session shortly.
                </p>

                <div className="pbp-confirmation-box">
                  <div className="pbp-conf-row">
                    <span className="pbp-conf-label">Booking ref</span>
                    <span className="pbp-conf-value">{bookingRef}</span>
                  </div>
                  <div className="pbp-conf-row">
                    <span className="pbp-conf-label">Session</span>
                    <span className="pbp-conf-value">{selectedSession?.name}</span>
                  </div>
                  <div className="pbp-conf-row">
                    <span className="pbp-conf-label">With</span>
                    <span className="pbp-conf-value">{profile.display_name}</span>
                  </div>
                  <div className="pbp-conf-row">
                    <span className="pbp-conf-label">Where</span>
                    <span className="pbp-conf-value" data-testid="public-booking-conf-location">
                      {locationLine(selectedSession || {})}
                    </span>
                  </div>
                  <div className="pbp-conf-row">
                    <span className="pbp-conf-label">Amount paid</span>
                    <span className="pbp-conf-value" data-testid="public-booking-amount-paid">
                      {(paidAmount ?? 0) > 0 ? `$${paidAmount.toFixed(2)}` : "Free"}
                    </span>
                  </div>
                  {(paidAmount ?? 0) > 0 && (
                    <div className="pbp-conf-row" style={{ fontSize: 11, color: "var(--ink-light)" }}>
                      <span className="pbp-conf-label" style={{ fontSize: 11 }}>Breakdown</span>
                      <span style={{ fontSize: 11, color: "var(--ink-light)" }} data-testid="public-booking-conf-breakdown">
                        ${(quote?.price ?? 0).toFixed(2)} session + ${(quote?.processing_fee ?? 0).toFixed(2)} card processing
                      </span>
                    </div>
                  )}
                </div>

                {/* Join Emergent CTA */}
                {profile.post_book_prompt && !joinDismissed && (
                  <>
                    <div className="pbp-join-card">
                      <p className="pbp-join-eyebrow">You found {profile.display_name} on</p>
                      <p className="pbp-join-title">11/11 Collective Connection</p>
                      <p className="pbp-join-body">
                        11/11 Collective Connection is a spiritual consciousness coaching app. Explore courses, tools, and practitioners like {profile.display_name.split(" ")[0]} — all in one place.
                      </p>
                      <a href="https://11-11cc.com/join" className="pbp-join-btn">
                        Join 11/11 Collective Connection <ArrowRight size={14} />
                      </a>
                    </div>
                    <span className="pbp-skip-link" onClick={() => setJoinDismissed(true)}>
                      No thanks, I'm all set
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}
